import {
  faBlockQuote,
  faCircleQuestion,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CourseHero } from '@/courses/CourseHero';
import { CourseInfoSidebar } from '@/courses/CourseInfoSidebar';
import { ItemMarks, PreviewLayout } from '@/preview/PreviewLayout';

export const Route = createFileRoute('/preview/')({
  component: PreviewCourse,
});

// The student course page's shape: hero, description, then the lessons, each
// opening to its topics and quiz, beside the course card.
function PreviewCourse() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number[]>([]);
  const toggle = (id: number) =>
    setOpen((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  return (
    <PreviewLayout sidebar={false}>
      {({ course, lessons }) => (
        <Scroll>
          <CourseHero
            title={course.title}
            imageUrl={course.cover_image || undefined}
          />
          <Columns>
            <Content>
              <Description
                // biome-ignore lint/security/noDangerouslySetInnerHtml: the course description the student course page renders the same way.
                dangerouslySetInnerHTML={{ __html: course.content }}
              />
              <ContentTitle>{t('lessons.course-content')}</ContentTitle>
              <Lessons>
                {lessons.map((lesson) => {
                  const isOpen = open.includes(lesson.id);
                  return (
                    <li key={lesson.id}>
                      <LessonButton
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => toggle(lesson.id)}
                      >
                        <Caret $open={isOpen}>
                          <FontAwesomeIcon icon={faCaretRight} />
                        </Caret>
                        <LessonName>
                          {lesson.title}
                          <ItemMarks
                            change={lesson.change}
                            published={lesson.published}
                          />
                        </LessonName>
                        <Meta>
                          {lesson.topics.length} {t('wbs.topics')}
                          {lesson.has_final_quiz && ` | 1 ${t('lessons.quiz')}`}
                        </Meta>
                      </LessonButton>
                      {isOpen && (
                        <Topics>
                          {lesson.topics.map((topic) => (
                            <li key={topic.id}>
                              <Link
                                to="/preview/topic/$topicId/"
                                params={{ topicId: String(topic.id) }}
                              >
                                <FontAwesomeIcon icon={faBlockQuote} />
                                {topic.title}
                                <ItemMarks
                                  change={topic.change}
                                  published={topic.published}
                                />
                              </Link>
                            </li>
                          ))}
                          {lesson.has_final_quiz && (
                            <li>
                              <Link
                                to="/preview/quiz/$lessonId/"
                                params={{ lessonId: String(lesson.id) }}
                              >
                                <FontAwesomeIcon icon={faCircleQuestion} />
                                {t('lessons.quiz')}
                              </Link>
                            </li>
                          )}
                        </Topics>
                      )}
                    </li>
                  );
                })}
              </Lessons>
            </Content>
            <CourseInfoSidebar
              imageUrl={course.thumbnail}
              // Counted as students will see them once published.
              lessonsCount={lessons.filter((l) => l.published).length}
              topicsCount={lessons
                .filter((l) => l.published)
                .reduce(
                  (n, l) =>
                    n + l.topics.filter((topic) => topic.published).length,
                  0,
                )}
              hasCertificate={course.has_certificate}
              requiresStudyHelper={course.study_helper_required}
              requiredSeries={course.required_series}
              progress={0}
              hasAccess
              unlockable={false}
              onlineForbidden={false}
              onlineForbiddenMessage=""
              onUnlock={() => {}}
              isUnlocking={false}
            />
          </Columns>
        </Scroll>
      )}
    </PreviewLayout>
  );
}

const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
`;

// As the student course page lays it out: the card overlaps the hero.
const Columns = styled.div`
  display: grid;
  gap: 40px;
  grid-template-columns: 1fr 360px;
  margin: 0 auto;
  max-width: 1200px;
  padding: 50px 20px 83px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Content = styled.div`
  min-width: 0;
`;

const Description = styled.div`
  color: var(--grey-900);
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 48px;
`;

const ContentTitle = styled.h2`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.5px;
  margin: 0 0 22px;
  text-transform: uppercase;
`;

const Lessons = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 14px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const LessonButton = styled.button`
  align-items: center;
  background: var(--grey-blue-300-tint);
  border: none;
  border-radius: 5px;
  color: var(--grey-900);
  cursor: pointer;
  display: flex;
  font-family: var(--open-sans);
  font-size: 16px;
  gap: 8px;
  padding: 16px 14px;
  text-align: left;
  width: 100%;

  &:hover {
    color: var(--wbs-red);
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  color: var(--grey-600);
  flex: none;
  transform: ${(p) => (p.$open ? 'rotate(90deg)' : 'none')};
  transition: transform 0.2s;
`;

const LessonName = styled.span`
  flex: 1;
`;

const Meta = styled.small`
  color: var(--grey-600);
  font-size: 12px;
  font-weight: 300;
  white-space: nowrap;
`;

const Topics = styled.ul`
  list-style: none;
  margin: 4px 0 0;
  padding: 0;

  a {
    align-items: center;
    color: var(--grey-600);
    display: flex;
    font-family: var(--open-sans);
    font-size: 14px;
    gap: 11px;
    padding: 12px 12px 12px 40px;
    text-decoration: none;
  }

  a:hover {
    background: var(--grey-150);
    color: var(--dark-wbs-red);
  }
`;
